if (!customElements.get('sticky-cta-bar')) {
  class StickyCtaBar extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.targetSelector = this.dataset.target || '.main-product__buy-buttons';
      this.observer = null;
      this.isVisible = false;
      this.mainSectionId = null;
      this.openInfoDrawerIds = new Set();

      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('.sticky-cta__submit');
      this.notifyButton = this.querySelector('.sticky-cta__notify');
      this.dynamicCheckout = this.querySelector('.sticky-cta__dynamic-checkout');
      this.notifyJsonEl = this.querySelector('.sticky-cta-notify-json');
      this.notifyProduct = this.notifyJsonEl ? JSON.parse(this.notifyJsonEl.textContent) : null;
      this.notifySizeOptionPosition = Number(this.dataset.notifySizeOptionPosition) || 0;
      this.sizeValueEl = this.querySelector('[data-size-value]');
      this.sizeOptionIndex = Number(this.sizeValueEl?.dataset.sizeOptionPosition ?? 0) - 1;
      this.sizeOptionName = this.sizeValueEl?.dataset.sizeOptionName ?? '';
      this.sizeOptions = this.querySelectorAll('[data-size-option]');
      this.quantityInput = this.querySelector('.sticky-cta__quantity-input');
      this.quantityButtons = this.querySelectorAll('.sticky-cta__quantity-button');
      this.priceCurrentEl = this.querySelector('[data-price-current]');
      this.priceCompareEl = this.querySelector('[data-price-compare]');
      this.variantInput = this.querySelector('input[name="id"]');

      this.currentVariant = this.notifyProduct?.variants?.find(
        (variant) => String(variant.id) === String(this.variantInput?.value)
      ) ?? null;

      this.handleSizeOptionClick = this.handleSizeOptionClick.bind(this);
      this.handleQuantityChange = this.handleQuantityChange.bind(this);
      this.handleQuantityButton = this.handleQuantityButton.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleNotifyClick = this.handleNotifyClick.bind(this);
      this.onExternalVariantChange = this.onExternalVariantChange.bind(this);
      this.onExternalQuantityChange = this.onExternalQuantityChange.bind(this);
      this.onInfoDrawerOpen = this.onInfoDrawerOpen.bind(this);
      this.onInfoDrawerClose = this.onInfoDrawerClose.bind(this);
    }

    connectedCallback() {
      this.resolveMainSectionId();
      this.initObserver();
      this.attachEventListeners();
      this.subscribeToPubSub();

      this._abortController = new AbortController();
      this._publishHeight();
      window.addEventListener('resize', theme.utils.debounce(() => this._publishHeight(), 100), {
        signal: this._abortController.signal,
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.detachEventListeners();
      this.unsubscribeFromPubSub();
      this._abortController?.abort();
      document.documentElement.style.setProperty('--sticky-cta-height', '0px');
      document.documentElement.removeAttribute('data-sticky-cta-top-visible');
    }

    _publishHeight() {
      const isVisible = this.isVisible && getComputedStyle(this).display !== 'none';
      const isTopVariant = this.classList.contains('sticky-cta--top');
      const height = isVisible && !isTopVariant ? this.offsetHeight : 0;
      document.documentElement.style.setProperty('--sticky-cta-height', `${height}px`);
    }

    resolveMainSectionId() {
      const target = document.querySelector(this.targetSelector);
      if (!target) return;

      const mainSection = target.closest('section[id]');
      if (mainSection) {
        this.mainSectionId = mainSection.id.replace('shopify-section-', '');
        return;
      }

      const productInfo = document.querySelector('product-info[data-section-id]');
      if (productInfo) {
        this.mainSectionId = productInfo.dataset.sectionId;
        return;
      }

      console.warn('[sticky-cta-bar] Could not resolve main product sectionId — sticky→main sync disabled');
    }

    initObserver() {
      const target = document.querySelector(this.targetSelector);
      if (!target) {
        console.warn(`[sticky-cta-bar] Target "${this.targetSelector}" not found`);
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.hide();
              return;
            }

            entry.boundingClientRect.top < 0 ? this.show() : this.hide();
          });
        },
        { threshold: 0 }
      );

      this.observer.observe(target);
    }

    attachEventListeners() {
      this.sizeOptions.forEach((option) => {
        option.addEventListener('click', this.handleSizeOptionClick);
      });

      this.quantityInput?.addEventListener('change', this.handleQuantityChange);

      this.quantityButtons.forEach((button) => {
        button.addEventListener('click', this.handleQuantityButton);
      });

      this.form?.addEventListener('submit', this.handleSubmit);
      this.notifyButton?.addEventListener('click', this.handleNotifyClick);
    }

    detachEventListeners() {
      this.sizeOptions.forEach((option) => {
        option.removeEventListener('click', this.handleSizeOptionClick);
      });

      this.quantityInput?.removeEventListener('change', this.handleQuantityChange);

      this.quantityButtons.forEach((button) => {
        button.removeEventListener('click', this.handleQuantityButton);
      });

      this.form?.removeEventListener('submit', this.handleSubmit);
      this.notifyButton?.removeEventListener('click', this.handleNotifyClick);
    }

    subscribeToPubSub() {
      if (!theme.pubsub) return;

      this.unsubscribeVariant = theme.pubsub.subscribe(
        theme.pubsub.events.VARIANT_CHANGE,
        this.onExternalVariantChange
      );

      this.unsubscribeQuantity = theme.pubsub.subscribe(
        theme.pubsub.events.QUANTITY_UPDATE,
        this.onExternalQuantityChange
      );

      this.unsubscribeInfoDrawerOpen = theme.pubsub.subscribe(
        theme.pubsub.events.DRAWER_OPEN,
        this.onInfoDrawerOpen
      );

      this.unsubscribeInfoDrawerClose = theme.pubsub.subscribe(
        theme.pubsub.events.DRAWER_CLOSE,
        this.onInfoDrawerClose
      );
    }

    unsubscribeFromPubSub() {
      this.unsubscribeVariant?.();
      this.unsubscribeQuantity?.();
      this.unsubscribeInfoDrawerOpen?.();
      this.unsubscribeInfoDrawerClose?.();
    }

    onInfoDrawerOpen(data) {
      if (!data?.id?.startsWith('PdpInfoDrawer-')) return;
      this.openInfoDrawerIds.add(data.id);
      this.classList.toggle('sticky-cta--drawer-open', this.openInfoDrawerIds.size > 0);
    }

    onInfoDrawerClose(data) {
      if (!data?.id?.startsWith('PdpInfoDrawer-')) return;
      this.openInfoDrawerIds.delete(data.id);
      this.classList.toggle('sticky-cta--drawer-open', this.openInfoDrawerIds.size > 0);
    }

    show() {
      if (this.isVisible) return;
      this.isVisible = true;
      this.classList.add('is-visible');
      this._publishHeight();
      this._syncHeaderVisibility();
    }

    hide() {
      if (!this.isVisible) return;
      this.isVisible = false;
      this.classList.remove('is-visible');
      this._publishHeight();
      this._syncHeaderVisibility();
    }

    _syncHeaderVisibility() {
      const isTopVariant = this.classList.contains('sticky-cta--top');
      document.documentElement.toggleAttribute('data-sticky-cta-top-visible', isTopVariant && this.isVisible);
    }

    handleSizeOptionClick(event) {
      const option = event.currentTarget;
      const value = option.dataset.sizeOption;
      const control = this.findMainOptionControl(value);

      option.closest('theme-dropdown')?.close?.();

      if (!control) return;

      if (control.tagName === 'SELECT') {
        control.value = value;
      } else {
        control.checked = true;
      }

      control.dispatchEvent(new Event('change', { bubbles: true }));
    }

    findMainOptionControl(value) {
      const picker = document.querySelector('variant-selects');
      if (!picker || !this.sizeOptionName) return null;

      const name = `options[${this.sizeOptionName}]`;
      const select = [...picker.querySelectorAll('select')].find((el) => el.name === name);
      if (select) return select;

      return [...picker.querySelectorAll('input[type="radio"]')].find(
        (el) => el.name === name && el.value === value
      );
    }

    handleQuantityChange() {
      this.validateQuantity();
      this.publishQuantityChange();
    }

    handleQuantityButton(event) {
      const button = event.currentTarget;
      const isPlus = button.name === 'plus';
      const currentValue = parseInt(this.quantityInput.value, 10) || 1;
      const min = parseInt(this.quantityInput.min, 10) || 1;
      const max = parseInt(this.quantityInput.max, 10) || Infinity;

      const newValue = Math.max(min, Math.min(max, isPlus ? currentValue + 1 : currentValue - 1));

      if (newValue !== currentValue) {
        this.quantityInput.value = newValue;
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    validateQuantity() {
      if (!this.quantityInput) return;

      let value = parseInt(this.quantityInput.value, 10);
      const min = parseInt(this.quantityInput.min, 10) || 1;
      const max = parseInt(this.quantityInput.max, 10) || Infinity;

      if (isNaN(value) || value < min) {
        value = min;
      } else if (value > max) {
        value = max;
      }

      this.quantityInput.value = value;
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (this.submitButton?.disabled) return;

      const variantId = this.variantInput?.value;
      if (!variantId) return;

      const quantity = parseInt(this.quantityInput?.value || '1', 10);

      theme.cart.errors.clearProductForm(variantId);

      this.setLoading(true);
      const cartDrawer = document.querySelector('cart-drawer');
      cartDrawer?.setLoading(true);

      try {
        const data = await theme.cart.add({ id: variantId, quantity }, { openDrawer: true });

        if (cartDrawer?.renderContents) {
          cartDrawer.renderContents(data);
        }
        cartDrawer?.setLoading(false);

        theme.pubsub?.publish(theme.pubsub.events.CART_UPDATE, {
          source: 'sticky-cta',
          productVariantId: variantId,
          cartData: data,
          skipFetch: true,
        });
      } catch (error) {
        cartDrawer?.setLoading(false);
        cartDrawer?.close();
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isLoading;
      this.submitButton.classList.toggle('loading', isLoading);
      this.submitButton.setAttribute('aria-busy', isLoading.toString());
    }

    updateVariantInput(variant) {
      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }
    }

    updatePrice(variant) {
      const displayPrice = theme.utils.employeePrice(variant.price, variant.compare_at_price, this.dataset.employeeDiscount);

      if (this.priceCurrentEl) {
        this.priceCurrentEl.textContent = theme.utils.formatMoney(displayPrice.price);
        this.priceCurrentEl.classList.toggle('sticky-cta__price-current--employee', displayPrice.employee);
      }

      if (this.priceCompareEl) {
        if (displayPrice.compareAtPrice != null) {
          this.priceCompareEl.textContent = theme.utils.formatMoney(displayPrice.compareAtPrice);
          this.priceCompareEl.classList.remove('is-hidden');
        } else {
          this.priceCompareEl.classList.add('is-hidden');
        }
      }
    }

    updateSizeValue(variant) {
      if (!this.sizeValueEl || this.sizeOptionIndex < 0) return;

      const value = variant?.options?.[this.sizeOptionIndex];
      if (!value) return;

      this.sizeValueEl.textContent = value;

      this.sizeOptions.forEach((option) => {
        const isSelected = option.dataset.sizeOption === value;
        option.setAttribute('aria-selected', isSelected.toString());
        option.classList.toggle('dropdown__option--active', isSelected);
      });
    }

    updateActionButtons(variant) {
      if (!this.submitButton) return;

      const buttonText = this.submitButton.querySelector('.sticky-cta__submit-text');

      if (!variant) {
        this.submitButton.classList.remove('hidden');
        this.notifyButton?.classList.add('hidden');
        this.dynamicCheckout?.classList.add('hidden');
        this.submitButton.disabled = true;
        if (buttonText) buttonText.textContent = theme.locales?.product?.unavailable;
        return;
      }

      if (variant.available) {
        this.submitButton.classList.remove('hidden');
        this.notifyButton?.classList.add('hidden');
        this.dynamicCheckout?.classList.remove('hidden');
        this.submitButton.disabled = false;
        if (buttonText) buttonText.textContent = theme.locales?.product?.addToCart;
      } else {
        this.submitButton.classList.add('hidden');
        this.notifyButton?.classList.remove('hidden');
        this.dynamicCheckout?.classList.add('hidden');
        this.submitButton.disabled = true;
      }
    }

    handleNotifyClick() {
      const drawer = document.querySelector('notify-me-drawer');
      if (!drawer) return;

      const payload = this.composeNotifyPayload();
      if (!payload) return;

      drawer.open(payload);
    }

    composeNotifyPayload() {
      const product = this.notifyProduct;
      const variant = this.currentVariant;
      if (!product || !variant) return null;

      const sizePosition = this.notifySizeOptionPosition;
      const options = variant.options || [];
      const size = sizePosition > 0 ? options[sizePosition - 1] : null;
      const variantLabel = options
        .filter((_, index) => index + 1 !== sizePosition)
        .join(' / ');
      const image = variant.featured_image?.src || product.featured_image || product.images?.[0];

      return {
        productId: product.id,
        variantId: variant.id,
        title: product.title,
        variant: variantLabel,
        size,
        image,
      };
    }

    publishQuantityChange() {
      theme.pubsub?.publish(theme.pubsub.events.QUANTITY_UPDATE, {
        source: 'sticky-cta',
        sectionId: this.mainSectionId ?? this.sectionId,
        quantity: parseInt(this.quantityInput?.value, 10) || 1,
      });
    }

    onExternalVariantChange(data) {
      if (data.source === 'sticky-cta') return;

      if (data.variant) {
        this.currentVariant = data.variant;
        this.updateVariantInput(data.variant);
        this.updatePrice(data.variant);
        this.updateSizeValue(data.variant);
        this.updateActionButtons(data.variant);
      }
    }

    onExternalQuantityChange(data) {
      if (data.source === 'sticky-cta') return;

      if (this.quantityInput && data.quantity) {
        this.quantityInput.value = data.quantity;
      }
    }
  }

  customElements.define('sticky-cta-bar', StickyCtaBar);
}
